import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { useResizeObserver } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import { Transition } from "../Transition";
import styles from "./Area.module.scss";

/**
 * Area provides a way to have a div transition to concealed or revealed states. The transition from
 * one state to the other can be animated or immediate. Area will determine if its content changes
 * and will adjust to accommodate.
 */

export const Area = (props) => {
  const {
    animate = true,
    animateInitialRender = true,
    children,
    className,
    duration = 250,
    fullHeight = false,
    id,
    onTransitionEnd,
    revealed = false,
    role,
  } = props;
  const [animating, setAnimating] = useState(animateInitialRender && animate && revealed);
  const [contentHeight, setContentHeight] = useState(0);
  const [visible, setVisible] = useState(revealed);
  const ref = useRef(null);
  const previousHeight = useRef(0);
  const isInitialRender = useRef(true);

  useResizeObserver({ callback: updateHeight, element: ref });

  useEffect(() => {
    if (!isInitialRender.current) {
      if (animate && revealed !== visible) {
        setAnimating(true);
      }

      if (revealed) {
        setVisible(true);
      }
    } else {
      isInitialRender.current = false;
    }
  }, [animate, revealed]);

  return (
    <Transition {...getTransitionProps()}>
      <div {...getContainerProps()}>{children}</div>
    </Transition>
  );

  function getTransitionProps() {
    const targetHeight = getTargetHeight();
    const initialHeight = animateInitialRender ? "0px" : targetHeight;

    return {
      animate: {
        height: targetHeight,
      },
      className: classNames(className, { [styles.area]: animating }),
      id,
      initial: { height: initialHeight },
      onTransformationEnd: handleTransformationEnd,
      transition: {
        duration: animate && (animateInitialRender || animating) ? duration / 1000 : 0,
      },
    };
  }

  function getContainerProps() {
    return {
      ["aria-hidden"]: !revealed,
      className: classNames({ [styles.fullHeight]: fullHeight, [styles.hidden]: !visible }),
      ref,
      role,
    };
  }

  function getTargetHeight() {
    let targetHeight;

    if (fullHeight) {
      targetHeight = revealed ? "100%" : "0px";
    } else {
      targetHeight = revealed && contentHeight ? `${contentHeight}px` : "0px";
    }

    return targetHeight;
  }

  function handleTransformationEnd(value) {
    const { height: eventHeight } = value;
    let transitionEnded = false;

    if (fullHeight) {
      transitionEnded = revealed ? eventHeight === "100%" : eventHeight === "0px";
    } else {
      const newHeight = parseFloat(eventHeight);
      const heightStabilized = newHeight === previousHeight.current;
      const reachedFinalHeight = revealed ? newHeight !== 0 : newHeight === 0;

      transitionEnded = revealed
        ? heightStabilized && reachedFinalHeight
        : !heightStabilized && reachedFinalHeight;
    }

    if (transitionEnded) {
      setAnimating(false);
      setVisible(revealed);
      onTransitionEnd?.(revealed);
    }
  }

  function updateHeight(value) {
    const newHeight = value[0].contentRect.height;

    if (newHeight !== previousHeight.current) {
      previousHeight.current = newHeight;

      if (!isInitialRender.current && animateInitialRender) {
        setAnimating(animate);
      }

      setContentHeight(newHeight);
    }
  }
};

Area.displayName = "Area";

export const areaPropTypes = {
  /** The transition to and from concealed/revealed states will be animated by default. */
  animate: PropTypes.bool,

  /** When set, Area will animate height when Area is first rendered. */
  animateInitialRender: PropTypes.bool,

  /** Content that will be rendered when Area is revealed. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /**
   * Length of animation in milliseconds. This value is simply passed through to Transform. For no
   * animation, specify zero. If no value is specified, then the default Spring easing will be used.
   */
  duration: PropTypes.number,

  /** Indicates if Area will fill/stretch to fill entire height of parent container. */
  fullHeight: PropTypes.bool,

  /**
   * id can be useful when another DOM element "controls" the concealed/revealed state. The
   * controlling element should use aria-control to reflect this correlation.
   */
  id: PropTypes.string,

  /**
   *  Function called when the area is revealed or concealed. The revealed state will be returned as
   *  a boolean.
   */
  onTransitionEnd: PropTypes.func,

  /**
   * Indicates if the area is concealed or revealed. Area does not maintain its revealed/concealed
   * state. It's up to the caller to control Area's state.
   */
  revealed: PropTypes.bool,

  /** The accessibility role for the content container. */
  role: PropTypes.string,
};

Area.propTypes = areaPropTypes;
